import React, { FC, useEffect, useState } from 'react';
import { CargoManagementQuery } from '../../../../services/query';
import Swal from 'sweetalert2';
import { Autocomplete, Box, FormControl, Grid, LinearProgress, TextField } from '@mui/material';
import { AddressEntityType, AddressEntryTypeId } from '../../../../types/enums/addressEntityType-enum';
import { useTranslation } from 'react-i18next';
import { updateCustomerAddressRequest } from '../../CustomerManagement.helper';
import {
    CAIP,
    caipCls,
    caipEntityAutocomplete,
    caipEntityFieldModifier,
    caipEntityTextfield,
} from './customerAddressInformationPaper.classes';

interface ICustomerAddressEntityFieldsProps {
    customerAddress: updateCustomerAddressRequest;
    setCustomerAddress: React.Dispatch<React.SetStateAction<updateCustomerAddressRequest>>;
    addressEntityTypeListByCountryId?: any;
}

const EMPTY_ADDRESS_ENTITY_DATA_TYPES = [
    AddressEntityType.District,
    AddressEntityType.Flat,
    AddressEntityType.Floor,
    AddressEntityType.House,
    AddressEntityType.Street,
    AddressEntityType.PostalCode,
    AddressEntityType.PersonalNumber,
];

const resetEmptyAddressEntityDataTypes = (
    rules: any[] | undefined,
    emptyTypes: number[],
    setAddressEntityTypeData: React.Dispatch<React.SetStateAction<Record<number, any>>>
) => {
    const typesToReset =
        rules?.filter((rule: any) => emptyTypes.includes(rule.addressEntityTypeId)).map((rule: any) => rule.addressEntityTypeId) ??
        [];

    if (typesToReset.length === 0) {
        return;
    }

    setAddressEntityTypeData((prevData) => {
        const nextData = { ...prevData };
        typesToReset.forEach((typeId: number) => {
            nextData[typeId] = [];
        });
        return nextData;
    });
};

export const CustomerAddressEntityFields: FC<ICustomerAddressEntityFieldsProps> = ({
    customerAddress,
    setCustomerAddress,
    addressEntityTypeListByCountryId,
}) => {
    let countryId_KZ = 10;
    let countryId_UA = 24;
    const { t } = useTranslation();
    const [addressEntityTypeData, setAddressEntityTypeData] = useState<Record<number, any>>({});
    const countryId = JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.countryId;
    const [isAllDataLoaded, setIsAllDataLoaded] = useState(false);
    enum CustomerAddressFields {
        PostalCode = 'postalCodeId',
        Region = 'countryZoneId',
        Neighborhood = 'neighborhoodId',
        County = 'countyId',
        City = 'cityId',
        Street = 'street',
        House = 'house',
        District = 'districtId',
        Flat = 'flat',
        PersonalNumber = 'personalNumber',
    }
    const GetPostalCodeList = async () => {
        try {
            CargoManagementQuery.GetPostalCodeList().then((res: any) => {
                let parseData = res.data.addressEntities?.map((item: any) => {
                    return {
                        value: item.addressEntityId,
                        text: item.name,
                        parentAddressEntityId: item.parentAddressEntityId,
                    };
                });
                setAddressEntityTypeData((prevData) => ({
                    ...prevData,
                    [AddressEntityType.PostalCode]: parseData,
                }));
            });
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        }
    };

    const GetRegion = async (value?: any) => {
        try {
            CargoManagementQuery.GetRegion({
                parentAddressEntityId: value ? [value] : null,
            }).then((res: any) => {
                let parseData = res.data.addressEntities.map((item: any) => {
                    return {
                        value: item.addressEntityId,
                        text: item.name,
                        parentAddressEntityId: item.parentAddressEntityId,
                    };
                });
                setAddressEntityTypeData((prevData) => ({
                    ...prevData,
                    [AddressEntityType.Region]: parseData,
                }));
            });
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        }
    };
    const GetDistrict = async (value?: any) => {
        try {
            CargoManagementQuery.GetDistrict({
                parentAddressEntityId: value ? [value] : null,
            }).then((res: any) => {
                let parseData = res.data.addressEntities.map((item: any) => {
                    return {
                        value: item.addressEntityId,
                        text: item.name,
                        parentAddressEntityId: item.parentAddressEntityId,
                    };
                });
                setAddressEntityTypeData((prevData) => ({
                    ...prevData,
                    [AddressEntityType.District]: parseData,
                }));
            });
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        }
    };
    const GetStreet = async (value?: any) => {
        try {
            CargoManagementQuery.GetStreet({
                parentAddressEntityId: value
                    ? [value]
                    : customerAddress.cityId !== null
                      ? [customerAddress.cityId]
                      : [null],
            }).then((res: any) => {
                let parseData = res.data.addressEntities.map((item: any) => {
                    return {
                        value: item.addressEntityId,
                        text: item.name,
                        parentAddressEntityId: item.parentAddressEntityId,
                    };
                });
                setAddressEntityTypeData((prevData) => ({
                    ...prevData,
                    [AddressEntityType.Street]: parseData,
                }));
            });
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        }
    };

    const GetCityList = async (value?: any) => {
        try {
            let parentAddressEntityId;

            if (value) {
                parentAddressEntityId = [value];
            }
            // KZ veya UA için districtId'yi kullan
            else if (countryId === countryId_KZ || countryId === countryId_UA) {
                parentAddressEntityId = customerAddress.districtId ? [customerAddress.districtId] : [null];
            } else {
                parentAddressEntityId =
                    customerAddress.countryZoneId !== null ? [customerAddress.countryZoneId] : [null];
            }
            CargoManagementQuery.CityList({
                addressEntityTypeId: 3,
                countryId,
                parentAddressEntityId: parentAddressEntityId,
            }).then((res: any) => {
                if (res.data?.addressEntities) {
                    let parseData = res.data.addressEntities?.map((item: any) => {
                        return {
                            value: item.addressEntityId,
                            text: item.name,
                            parentAddressEntityId: item.parentAddressEntityId,
                        };
                    });
                    setAddressEntityTypeData((prevData) => ({
                        ...prevData,
                        [AddressEntityType.City]: parseData,
                    }));
                }
            });
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        }
    };

    const GetCountyList = async (value?: any) => {
        try {
            CargoManagementQuery.CountyList({
                parentAddressEntityIdList: value ? [value] : [customerAddress?.cityId],
                countryId,
            }).then((res: any) => {
                if (res.data?.addressEntities) {
                    let parseData = res.data.addressEntities?.map((item: any) => {
                        return { value: item.addressEntityId, text: item.name };
                    });
                    setAddressEntityTypeData((prevData) => ({
                        ...prevData,
                        [AddressEntityType.County]: parseData,
                    }));
                }
            });
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        }
    };

    const GetSubCountyList = async (value?: any) => {
        try {
            let params = {
                parentAddressEntityId: value ? value : customerAddress?.countyId,
                countryId,
            };
            if (customerAddress?.countyId !== 0) {
                CargoManagementQuery.SubAddressEntities(params).then((res: any) => {
                    if (res.data.addressEntities) {
                        let parseData = res.data.addressEntities?.map((item: any) => {
                            return {
                                value: item.addressEntityId,
                                text: item.name,
                            };
                        });
                        setAddressEntityTypeData((prevData) => ({
                            ...prevData,
                            [AddressEntityType.Neighborhood]: parseData,
                        }));
                    }
                });
            }
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        }
    };

    const getValueForEntityType = (entityTypeId: number, entryType?: string): string | undefined => {
        switch (entityTypeId) {
            case AddressEntityType.PostalCode:
                // PostalCode value depends on entry type (Text -> postalCode, Selection -> postalCodeId)
                return entryType === 'Text'
                    ? (customerAddress as any)?.postalCode
                    : (customerAddress as any)?.postalCodeId;
            case AddressEntityType.Region:
                return customerAddress?.countryZoneId;
            case AddressEntityType.City:
                return customerAddress?.cityId;
            case AddressEntityType.County:
                return customerAddress?.countyId;
            case AddressEntityType.Neighborhood:
                return customerAddress?.neighborhoodId;
            case AddressEntityType.Street:
                return entryType === 'Text' ? (customerAddress as any)?.streetName : (customerAddress as any)?.streetId;
            case AddressEntityType.House:
                return customerAddress?.house;
            case AddressEntityType.District:
                return customerAddress?.districtId;
            case AddressEntityType.Flat:
                return customerAddress?.flat;
            case AddressEntityType.PersonalNumber:
                return customerAddress?.personalNumber;
            default:
                return undefined;
        }
    };

    const createFormElement = (rule: any, data: Record<number, any>) => {
        const entityType = AddressEntityType[rule.addressEntityTypeId];
        const entryType = AddressEntryTypeId[rule.addressEntryTypeId];
        const value = getValueForEntityType(rule.addressEntityTypeId, entryType);

        switch (entryType) {
            case 'Text':
                return (
                    <FormControl fullWidth variant="outlined">
                        <TextField
                            className={caipEntityTextfield(rule.addressEntityTypeId)}
                            label={t(`addressDetail.${entityType}`)}
                            variant="outlined"
                            fullWidth
                            value={value}
                            onChange={(e: any) => handleChangeWithRule(rule, e.target.value, entryType)}
                        />
                    </FormControl>
                );
            case 'Selection':
            case 'AllInOneSelection':
                return (
                    <Autocomplete
                        className={caipEntityAutocomplete(rule.addressEntityTypeId)}
                        fullWidth
                        options={data[rule.addressEntityTypeId] || []}
                        getOptionLabel={(option: any) => option?.text || ''}
                        isOptionEqualToValue={(option: any, value: any) => option?.value === value?.value}
                        value={(data[rule.addressEntityTypeId] || []).find((x: any) => x.value === value) || null}
                        onChange={(_, newValue: any) => {
                            handleChangeWithRule(rule, newValue?.value ?? '', entryType);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t(`addressDetail.${entityType}`)}
                                variant="outlined"
                                className={caipEntityAutocomplete(rule.addressEntityTypeId)}
                            />
                        )}
                    />
                );
            case 'Hidden':
                return <></>;
            case 'Hybrid':
                return <></>;
            default:
                return <></>;
        }
    };

    useEffect(() => {
        CountryAddressRulesforCountryId();
    }, []);

    const CountryAddressRulesforCountryId = async () => {
        try {
            setIsAllDataLoaded(false);

            if (addressEntityTypeListByCountryId && addressEntityTypeListByCountryId.length > 0) {
                const servicePromises: Promise<any>[] = [];
                addressEntityTypeListByCountryId.forEach((rule: any) => {
                    const entryType = AddressEntryTypeId[rule.addressEntryTypeId];

                    if (entryType === 'Selection' || entryType === 'AllInOneSelection') {
                        if (rule.addressEntityTypeId === AddressEntityType.PostalCode) {
                            servicePromises.push(GetPostalCodeList());
                        } else if (rule.addressEntityTypeId === AddressEntityType.City) {
                            servicePromises.push(GetCityList());
                        } else if (rule.addressEntityTypeId === AddressEntityType.County) {
                            servicePromises.push(GetCountyList());
                        } else if (rule.addressEntityTypeId === AddressEntityType.Neighborhood) {
                            servicePromises.push(GetSubCountyList());
                        } else if (rule.addressEntityTypeId === AddressEntityType.Region) {
                            servicePromises.push(GetRegion());
                        } else if (rule.addressEntityTypeId === AddressEntityType.Street) {
                            servicePromises.push(GetStreet());
                        } else if (rule.addressEntityTypeId === AddressEntityType.District) {
                            servicePromises.push(GetDistrict());
                        }
                    } else {
                        resetEmptyAddressEntityDataTypes(
                            addressEntityTypeListByCountryId,
                            EMPTY_ADDRESS_ENTITY_DATA_TYPES,
                            setAddressEntityTypeData
                        );
                    }
                });

                if (servicePromises.length > 0) {
                    await Promise.all(servicePromises);
                }
            }

            setIsAllDataLoaded(true);
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
            setIsAllDataLoaded(false);
        }
    };

    const getFieldForEntityType = (entityTypeId: number): string => {
        switch (entityTypeId) {
            case AddressEntityType.PostalCode:
                return CustomerAddressFields.PostalCode;
            case AddressEntityType.Region:
                return CustomerAddressFields.Region;
            case AddressEntityType.City:
                return CustomerAddressFields.City;
            case AddressEntityType.County:
                return CustomerAddressFields.County;
            case AddressEntityType.Neighborhood:
                return CustomerAddressFields.Neighborhood;
            case AddressEntityType.House:
                return CustomerAddressFields.House;
            case AddressEntityType.District:
                return CustomerAddressFields.District;
            case AddressEntityType.Flat:
                return CustomerAddressFields.Flat;
            case AddressEntityType.PersonalNumber:
                return CustomerAddressFields.PersonalNumber;
            case AddressEntityType.Street:
                return CustomerAddressFields.Street;
            default:
                return '';
        }
    };

    const getFieldForEntityTypeWithEntry = (entityTypeId: number, entryType?: string): string => {
        if (entityTypeId === AddressEntityType.PostalCode) {
            return entryType === 'Text' ? 'postalCode' : 'postalCodeId';
        }
        if (entityTypeId === AddressEntityType.Street) {
            return entryType === 'Text' ? 'streetName' : 'streetId';
        }
        return getFieldForEntityType(entityTypeId);
    };

    const handleChangeWithRule = (rule: any, value: any, entryType?: string) => {
        const entityTypeId = rule.addressEntityTypeId;
        const field = getFieldForEntityTypeWithEntry(entityTypeId, entryType);
        if (field) {
            setCustomerAddress((prevAddress: any) => ({
                ...prevAddress,
                [field]: value,
            }));
        }

        const childEntities = getChildEntitiesFromCountryRules(entityTypeId);
        if (childEntities.length > 0) {
            resetAndLoadData(childEntities, value, getApiMethodForEntityType(entityTypeId));
        }
    };

    const getChildEntitiesFromCountryRules = (parentEntityTypeId: number): number[] => {
        if (!addressEntityTypeListByCountryId) return [];

        return addressEntityTypeListByCountryId
            .filter((rule: any) => rule.parentAddressEntityTypeId === parentEntityTypeId)
            .map((rule: any) => rule.addressEntityTypeId);
    };

    const getAllChildEntitiesFromCountryRules = (parentEntityTypeId: number): number[] => {
        const directChildren = getChildEntitiesFromCountryRules(parentEntityTypeId);
        const allChildren = [...directChildren];

        directChildren.forEach((childId) => {
            allChildren.push(...getAllChildEntitiesFromCountryRules(childId));
        });

        return allChildren;
    };

    const getApiMethodForEntityType = (entityTypeId: number): string => {
        switch (entityTypeId) {
            case AddressEntityType.Region:
                return countryId === countryId_KZ || countryId === countryId_UA ? 'GetDistrict' : 'GetCityList';
            case AddressEntityType.City:
                return countryId === countryId_UA ? '' : 'GetCountyList';
            case AddressEntityType.County:
            case AddressEntityType.Neighborhood:
                return 'GetSubCountyList';
            case AddressEntityType.Street:
                return 'GetStreet';
            case AddressEntityType.District:
                return 'GetCityList';
            default:
                return '';
        }
    };

    const resetAndLoadData = (entityTypesToReset: number[], parentValue: any, apiMethod: string) => {
        const allEntitiesToReset = entityTypesToReset.reduce((acc, entityType) => {
            acc.push(entityType);
            acc.push(...getAllChildEntitiesFromCountryRules(entityType));
            return acc;
        }, [] as number[]);

        const uniqueEntitiesToReset = Array.from(new Set(allEntitiesToReset));

        setCustomerAddress((prevAddress: any) => {
            const updatedAddress = { ...prevAddress };
            uniqueEntitiesToReset.forEach((entityType) => {
                const fieldToReset = getFieldForEntityType(entityType);
                if (fieldToReset) {
                    updatedAddress[fieldToReset] = '';
                }
            });
            return updatedAddress;
        });

        setAddressEntityTypeData((prev) => {
            const updated = { ...prev };
            uniqueEntitiesToReset.forEach((entityType) => {
                updated[entityType] = [];
            });
            return updated;
        });

        if (parentValue && apiMethod) {
            entityTypesToReset.forEach((entityTypeId) => {
                loadDataForNextLevel(entityTypeId, parentValue, apiMethod);
            });
        }
    };

    const loadDataForNextLevel = async (entityTypeId: number, parentValue: any, apiMethod: string) => {
        try {
            switch (apiMethod) {
                case 'GetRegion':
                    await GetRegion(parentValue);
                    break;
                case 'GetCityList':
                    await GetCityList(parentValue);
                    break;
                case 'GetCountyList':
                    await GetCountyList(parentValue);
                    break;
                case 'GetSubCountyList':
                    await GetSubCountyList(parentValue);
                    break;
                case 'GetStreet':
                    await GetStreet(parentValue);
                    break;
                case 'GetDistrict':
                    await GetDistrict(parentValue);
                    break;
                default:
                    return;
            }
        } catch (error) {
            console.error(`Error loading data with ${apiMethod}:`, error);
            setAddressEntityTypeData((prev) => ({
                ...prev,
                [entityTypeId]: [],
            }));
        }
    };

    return (
        <Grid
            className={CAIP.entityFieldsContainer}
            sx={{ marginTop: 0.5 }}
            size={{
                xs: 12,
                md: 12,
                lg: 12,
                xl: 12,
            }}
        >
            {isAllDataLoaded && Object.keys(addressEntityTypeData).length === addressEntityTypeListByCountryId?.length
                ? addressEntityTypeListByCountryId?.map((rule: any) => (
                      <div
                          key={rule.countryAddressRuleId}
                          className={caipCls(CAIP.entityFieldRow, caipEntityFieldModifier(rule.addressEntityTypeId))}
                          style={{ marginBottom: '16px' }}
                      >
                          {createFormElement(rule, addressEntityTypeData)}
                      </div>
                  ))
                : addressEntityTypeListByCountryId?.map((rule: any, index: number) => (
                      <Box
                          key={`loading-${rule.countryAddressRuleId || index}`}
                          className={CAIP.entityFieldLoading}
                          sx={{
                              display: 'flex',
                              alignItems: 'center',
                              marginBottom: '16px',
                              padding: '12px',
                              border: '1px solid #e0e0e0',
                              borderRadius: '4px',
                              backgroundColor: '#f9f9f9',
                          }}
                      >
                          <LinearProgress sx={{ width: '100%', height: '8px' }} />
                      </Box>
                  ))}
        </Grid>
    );
};
